import { supabase } from './supabase'
import { logActivity } from './activityService'

// Sign up new user
export const signUp = async ({ username, email, password, fullName, phone, address }) => {
  try {
    // Check if username exists
    const { data: existingUser } = await supabase
      .from('users')
      .select('username')
      .eq('username', username)
      .maybeSingle()

    if (existingUser) {
      throw new Error('Username already exists')
    }

    // Create auth user
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          username,
          full_name: fullName || ''
        }
      }
    })

    if (authError) throw authError

    // INSERT (not update) user profile into users table
    if (authData.user) {
      const { error: profileError } = await supabase
        .from('users')
        .insert({
          id: authData.user.id, // Use the auth user's ID
          username,
          email,
          full_name: fullName || '',
          name: fullName || username, // Add name field
          phone: phone || '',
          address: address || '',
          role: 'user',
          balance: 0,
          status: 'active',
          created_at: new Date().toISOString()
        })

      if (profileError) {
        console.error('Profile creation error:', profileError)
        // Rollback auth user if profile creation fails
        await supabase.auth.admin.deleteUser(authData.user.id)
        throw new Error('Failed to create user profile')
      }

      // Log activity
      try {
        await logActivity(authData.user.id, 'register', 'User registered')
      } catch (error) {
        console.error('Activity log error:', error)
      }
    }

    return { data: authData, error: null }
  } catch (error) {
    return { data: null, error: error.message }
  }
}

// Sign in user
export const signIn = async ({ email, password }) => {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    })

    if (error) throw error

    // Update last login
    if (data.user) {
      await supabase
        .from('users')
        .update({ last_login: new Date().toISOString() })
        .eq('id', data.user.id)

      // Log activity
      try {
        await logActivity(data.user.id, 'login', 'User logged in')
      } catch (error) {
        console.error('Activity log error:', error)
      }
    }

    return { data, error: null }
  } catch (error) {
    try {
      await logActivity(null, 'failed_login', `Failed login attempt: ${email}`)
    } catch (logError) {
      console.error('Activity log error:', logError)
    }
    return { data: null, error: error.message }
  }
}

// Sign out user
export const signOut = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    
    if (user) {
      try {
        await logActivity(user.id, 'logout', 'User logged out')
      } catch (error) {
        console.error('Activity log error:', error)
      }
    }

    const { error } = await supabase.auth.signOut()
    if (error) throw error

    return { error: null }
  } catch (error) {
    return { error: error.message }
  }
}

// Get current session
export const getSession = async () => {
  try {
    const { data: { session }, error } = await supabase.auth.getSession()
    if (error) throw error
    return { session, error: null }
  } catch (error) {
    return { session: null, error: error.message }
  }
}

// Get current user
export const getCurrentUser = async () => {
  try {
    const { data: { user }, error: authError } = await supabase.auth.getUser()
    if (authError) throw authError
    if (!user) return { user: null, profile: null, error: null }

    // Get user profile from users table
    const { data: profile, error: profileError } = await supabase
      .from('users')
      .select('*')
      .eq('id', user.id)
      .single()

    if (profileError && profileError.code !== 'PGRST116') {
      // PGRST116 is "not found" error - ignore it
      throw profileError
    }

    return { user, profile, error: null }
  } catch (error) {
    return { user: null, profile: null, error: error.message }
  }
}

// Update user profile
export const updateProfile = async (userId, updates) => {
  try {
    const { data, error } = await supabase
      .from('users')
      .update(updates)
      .eq('id', userId)
      .select()
      .single()

    if (error) throw error

    try {
      await logActivity(userId, 'profile_update', 'User updated profile')
    } catch (error) {
      console.error('Activity log error:', error)
    }

    return { data, error: null }
  } catch (error) {
    return { data: null, error: error.message }
  }
}

// Change password
export const changePassword = async (newPassword) => {
  try {
    const { data, error } = await supabase.auth.updateUser({
      password: newPassword
    })

    if (error) throw error

    if (data.user) {
      try {
        await logActivity(data.user.id, 'password_change', 'User changed password')
      } catch (error) {
        console.error('Activity log error:', error)
      }
    }

    return { data, error: null }
  } catch (error) {
    return { data: null, error: error.message }
  }
}

// Reset password request
export const requestPasswordReset = async (email) => {
  try {
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`
    })

    if (error) throw error

    return { data, error: null }
  } catch (error) {
    return { data: null, error: error.message }
  }
}

// Verify email
export const verifyEmail = async (token) => {
  try {
    const { data, error } = await supabase.auth.verifyOtp({
      token_hash: token,
      type: 'email'
    })

    if (error) throw error

    return { data, error: null }
  } catch (error) {
    return { data: null, error: error.message }
  }
}

// Check if user is admin
export const isAdmin = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('users')
      .select('role')
      .eq('id', userId)
      .single()

    if (error) throw error

    return data.role === 'admin'
  } catch (error) {
    return false
  }
}

// Listen to auth state changes
export const onAuthStateChange = (callback) => {
  return supabase.auth.onAuthStateChange(callback)
}

export default {
  signUp,
  signIn,
  signOut,
  getSession,
  getCurrentUser,
  updateProfile,
  changePassword,
  requestPasswordReset,
  verifyEmail,
  isAdmin,
  onAuthStateChange
}